import nl from "date-fns/locale/nl";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { getLocalDateString, months } from "shared/utils/date";
import "app/styles/date-picker.scss";
import { useOrderContext } from "app/providers/OrderProvider";
import moment from "moment";
import { getMonth, getYear } from "date-fns";
import { concatClassnames } from "shared/utils/concatClassnames";
import Icon, { IconTypes } from "shared/ui/icon";
import Spinner from "shared/ui/spinner";
import Text from "shared/ui/typography/Text";
import classNames from "classnames";
import { RightArrow } from "shared/ui/icons/RightArrow";
import { is } from "date-fns/locale";

/* Importing the nl locale from the date-fns library and registering it with the registerLocale function. */
registerLocale("nl", nl);

export type BumbalSlot = {
  dateFrom: string;
  dateTo: string;
  price: number;
};

interface BumbalProps {
  slots: BumbalSlot[];
  selectedDate: Date | null;
  setSelectedDate: Dispatch<SetStateAction<Date | null>>;
  refetchDates: (firstMonth: number, firstYear: number, type: string) => void;
  refetchAmount: number;
  disabled?: boolean;
  isBumbalDataLoading?: boolean;
  isBumbalDataFetching?: boolean;
}

const Bumbal = ({
  slots,
  selectedDate,
  setSelectedDate,
  refetchDates,
  refetchAmount,
  disabled = false,
  isBumbalDataLoading,
  isBumbalDataFetching,
}: BumbalProps) => {
  // TODO: Extract this to provider
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [fetchedMonths, setFetchedMonths] = useState<number[]>([]);
  const [showUnavailable, setShowUnavailable] = useState<boolean>(false);
  const [prevArrowDisabled, setPrevArrowDisabled] = useState<boolean>(true);
  const [openDropDown, setOpenDropDown] = useState<boolean>(false);

  const { order, type } = useOrderContext();

  const [bumbalText, setBumbalText] = useState<string>("Jouw inmeetafspraak");

  const thisDate = new Date();
  const thisYear = thisDate.getFullYear();
  const thisMonth = thisDate.getMonth();

  const ref = useRef<any>();

  /* Adding an event listener to the document that will call the handleOutsideDropClick function when the user clicks
  anywhere on the page. */
  useEffect(() => {
    document.addEventListener("click", handleOutsideDropClick);
    return () => {
      document.removeEventListener("click", handleOutsideDropClick);
    };
  });

  /* This effect sets the Bumbal text based on the URL query parameter 'type'.
  If the parameter is set to 'placement', the text is set to the 'your_montage' translation.
  Otherwise, the text remains the same as the default. */
  useEffect(() => {
    if (order?.isZakelijkOrder) {
      setBumbalText("Jouw bezorgafspraak");
    } else {
      if (type === "placement") {
        setBumbalText("Jouw montageafspraak");
      } else if (type === "shipment") {
        setBumbalText("Jouw afleverafspraak");
      }
    }
  }, [type, order?.isZakelijkOrder]);

  /* This effect creates a set of unique month numbers based on the date of each slot.
  If the set of months is different from the previously fetched months, it updates the fetched months.
  This is used to control which months are displayed in the calendar, to avoid unnecessary API calls.*/
  useEffect(() => {
    /* Creating a new Set object that can only contain numbers. */
    const monthSet = new Set<number>();

    /* Creating a set of months from the slots array. */
    slots.forEach((slot) => {
      const slotMonth = moment(slot.dateFrom).month() + 1;
      monthSet.add(slotMonth);
    });

    /* Creating an array from a set. */
    const months = Array.from(monthSet);

    if (months.length > 0 && months.length !== fetchedMonths.length) {
      setFetchedMonths(months);
    }
  }, [slots]);

  useEffect(() => {
    if (!currentDate || fetchedMonths.includes(currentDate.getMonth() + 1)) {
      return;
    }

    refetchDates(
      currentDate.getMonth() + 1,
      currentDate.getFullYear(),
      "click",
    );
  }, [currentDate]);

  /* This function handles the change in the current date state and updates the state for whether the previous month arrow should be disabled based on the new date.
  It uses the useCallback hook to memoize the function and optimize performance by preventing unnecessary re-renders.
  The openDropDown state is also set to false to ensure that the dropdown is closed when the current date is changed. */
  const handleCurrentDateChange = useCallback(
    (newCurrentDate: Date) => {
      setOpenDropDown(false);
      const currentMonth = thisMonth + 1;
      const currentMonthIsEqualToNewDate =
        currentMonth === newCurrentDate.getMonth() + 1;
      setPrevArrowDisabled(currentMonthIsEqualToNewDate);
    },
    [thisMonth],
  );

  /* A React hook that is used to run a function when the currentDate changes. */
  useEffect(() => {
    handleCurrentDateChange(currentDate);
  }, [currentDate, handleCurrentDateChange]);

  useEffect(() => {
    if (slots.length !== 0) {
      const month = currentDate.getMonth() + 1;
      let include = fetchedMonths.includes(month);
      if (!include) {
        setShowUnavailable(true);
      } else {
        setShowUnavailable(false);
      }
      if (fetchedMonths) {
        if (selectedDate === null) {
          setSelectedDate(availableDates[0]);
          setCurrentDate(availableDates[0]);
        }
      }
    }
  }, [fetchedMonths, currentDate, slots]);

  /* Mapping the available dates from the slots array. */
  const availableDates = slots.map(
    (slot) => new Date(slot.dateFrom.split(" ")[0]),
  );

  /* Using the useMemo hook to memoize the formattedDate. */
  const formattedDate = useMemo(() => {
    return selectedDate ? getLocalDateString(selectedDate) : "";
  }, [selectedDate]);

  /**
   * ChangeSelectedDate is a function that takes a date and sets the selected date to that date.
   * @param {Date | null} date - The date that was selected.
   */
  const changeSelectedDate = (date: Date | null) => {
    setSelectedDate(date);
  };

  /**
   * If the user clicks outside of the dropdown, close the dropdown
   * @param {any} e - any - this is the event that is triggered when the user clicks outside of the dropdown.
   */
  const handleOutsideDropClick = (e: any) => {
    if (ref.current && !ref.current.contains(e.target)) {
      if (e.target.tagName !== "svg") {
        setOpenDropDown(false);
      }
    }
  };

  /**
   * If the month of the last slot is less than the month of the next arrow, refetch the dates
   * @param {Date} date - The date that the calendar is currently on
   * @param {string} type - string - this is the type of calendar you want to use. It can be either 'single' or 'range'.
   */
  const nextArrowHandler = (date: Date, type: string) => {
    if (refetchAmount <= 5) {
      let month = getMonth(date) + 2;
      let year = getYear(date);
      if (month > 12) {
        month = 1;
        year = year + 1;
      }

      let last = slots[slots.length - 1];
      let lastMonth = moment(last.dateFrom).month() + 1;
      let lastYear = moment(last.dateFrom).year();

      if (lastMonth < month || lastYear < year) {
        if (!fetchedMonths.includes(month)) {
          refetchDates(month, year, type);
        }
      }
    }
  };

  /**
   * "If the current month is the last available month, then return true, otherwise return false."
   * The function is used to determine whether the next arrow should be disabled or not
   * @param {Date} date - Date - The current date that the calendar is set to.
   * @returns A boolean value.
   */
  const nextArrowDisabled = (date: Date): boolean => {
    const availableMonths = availableDropDownMonths();
    const lastAvailableMonth = availableMonths[availableMonths.length - 1];
    const currentMonth = months[date.getMonth()];
    return lastAvailableMonth === currentMonth;
  };

  /**
   * Handles selection of month in the dropdown menu
   * @param month - the month that was selected
   * @param year - the year that was selected
   * @param type - the type of selection (start or end date)
   */
  const dropDownHandler = (month: number, year: number, type: string) => {
    const shouldRefetch = refetchAmount <= 5 && !fetchedMonths.includes(month);
    if (shouldRefetch) {
      refetchDates(month, year, type);
    }
  };

  /**
   * It returns an array of months that are available for booking
   * @returns An array of strings.
   */
  const availableDropDownMonths = () => {
    // Declaring an array to hold the available months.
    const selectMonths: string[] = [];

    // Determine the month of the first available slot.
    const firstMonth = slots.length
      ? moment(slots[0].dateFrom).month() + 5
      : thisMonth + 5;

    // Add months to the selection based on the range of available months.
    if (firstMonth > 11) {
      // Add the months from the current month to December of the current year.
      selectMonths.push(...months.slice(thisMonth));
      // Add the months from January to the first available month of the next year.
      selectMonths.push(...months.slice(0, firstMonth - 12));
    } else {
      // Add the months from the current month to the first available month.
      selectMonths.push(...months.slice(thisMonth, firstMonth));
      // If the current month is December, also add the months from January to the first available month.
      if (thisMonth === 11) {
        selectMonths.push(...months.slice(0, firstMonth));
      }
    }

    return selectMonths;
  };

  /**
   * It returns an array of years that corresponds to the months in the dropdown menu
   * @returns An array of years.
   */
  const getDropdownYear = () => {
    // Get the list of months available in the dropdown
    const dropMonths = availableDropDownMonths();

    // Create an array of the current year, the length of the dropMonths array.
    let selectYears = new Array(dropMonths.length).fill(thisYear);

    if (dropMonths.includes(months[11])) {
      const decemberId = dropMonths.indexOf(months[11]);

      selectYears = selectYears.map((year, id) => {
        if (id > decemberId) return year + 1;

        return year;
      });
    }

    // Find the index of the first month in the months array that is included in the dropMonths array.
    const first = months.findIndex((m) => dropMonths.includes(m));

    // Find the index of the last month in the dropMonths array.
    const last = months.findIndex((m) => dropMonths.includes(m, first + 1));

    // Generate an array of years that corresponds to the months in the dropdown
    return [
      ...selectYears,
      ...(first > last
        ? new Array(12 - first).fill(thisYear + 1)
        : new Array(last - first + 1).fill(thisYear)),
    ];
  };

  /**
   * If the dropdown is open, close it. If the dropdown is closed, open it
   */
  const handleOpenDropDown = () => {
    setOpenDropDown(!openDropDown);
  };

  /**
   * If the day does not belong to the selected month, return an empty div, otherwise, render the day number
   * @param {number} day - The day number of the month.
   * @param {Date} date - The date of the day being rendered
   * @returns A function that takes in a day and a date and returns a div.
   */
  const renderDayContents = (day: number, date: Date) => {
    // If the day does not belong to the selected month, return an empty div
    if (date.getMonth() !== currentDate?.getMonth()) {
      return <div></div>;
    }
    // Otherwise, render the day number
    return <div>{day}</div>;
  };

  const loadingStatus =
    isBumbalDataFetching ||
    isBumbalDataLoading ||
    (slots.length === 0 && refetchAmount <= 5);

  return (
    <div className={`bumble-widget ${disabled ? "disabled" : ""}`}>
      <div className="gg-grid gg-w-full gg-grid-cols-12 gg-divide-y gg-divide-dark-blue gg-bg-grey-500 md:gg-divide-x md:gg-divide-y-0">
        <div className="gg-relative gg-col-span-12 gg-px-1 gg-py-5 sm:gg-px-8 sm:gg-py-8">
          <div className={loadingStatus ? "gg-opacity-0" : ""}>
            <DatePicker
              inline
              disabled={disabled}
              readOnly={disabled}
              className={loadingStatus ? "gg-relative" : "gg-react-datepicker"}
              selected={selectedDate}
              onChange={(date) => {
                if (!disabled) {
                  changeSelectedDate(date);
                }
              }}
              open={true}
              nextMonthButtonLabel=">"
              previousMonthButtonLabel="<"
              locale={"nl"}
              includeDates={availableDates}
              disabledKeyboardNavigation
              renderDayContents={renderDayContents}
              onMonthChange={(date) => setCurrentDate(date)}
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                changeMonth,
                changeYear,
              }) => (
                <div
                  className={
                    "gg-mb-8 gg-flex gg-justify-between gg-bg-transparent gg-text-left gg-font-bold"
                  }
                >
                  <button
                    onClick={decreaseMonth}
                    disabled={prevArrowDisabled || disabled}
                    className={classNames(
                      "gg-ml-2 gg-flex gg-items-center gg-justify-between gg-gap-3 gg-whitespace-nowrap gg-rounded-sm gg-px-5 gg-text-slate-800 gg-transition gg-duration-300 gg-ease-in-out hover:gg-scale-105 sm:gg-ml-0 sm:gg-w-28",
                      {
                        "gg-invisible": prevArrowDisabled,
                      },
                    )}
                  >
                    <span className="gg-rotate-180">
                      <RightArrow width={16} height={12} />
                    </span>
                    <span className="gg-hidden sm:gg-flex">Vorige</span>
                  </button>

                  <div
                    ref={ref}
                    className={concatClassnames(
                      "gg-px-y gg-absolute gg-z-10 gg-px-5",
                      "gg-left-0 gg-right-0 gg-mx-auto",
                      "gg-w-max gg-text-left gg-text-lg",
                      openDropDown ? "gg-bg-grey-600" : "",
                    )}
                  >
                    {openDropDown ? (
                      <ul>
                        {availableDropDownMonths().map(
                          (option, index) =>
                            months[currentDate.getMonth()] !== option &&
                            availableDropDownMonths().indexOf(option) <
                              availableDropDownMonths().indexOf(
                                months[currentDate.getMonth()],
                              ) && (
                              <li
                                className={
                                  index === 0
                                    ? "hover:gg-cursor-pointer"
                                    : "hover:gg-cursor-pointer"
                                }
                                onClick={() => {
                                  changeMonth(months.indexOf(option));
                                  changeYear(
                                    getDropdownYear()[
                                      availableDropDownMonths().indexOf(option)
                                    ],
                                  );
                                  dropDownHandler(
                                    months.indexOf(option) + 1,
                                    getDropdownYear()[
                                      availableDropDownMonths().indexOf(option)
                                    ],
                                    "click",
                                  );
                                }}
                                key={option}
                                value={option}
                              >
                                {option + " " + getDropdownYear()[index]}
                              </li>
                            ),
                        )}
                      </ul>
                    ) : null}
                    <button
                      onClick={handleOpenDropDown}
                      className={concatClassnames(
                        !openDropDown ||
                          availableDropDownMonths()[0] ===
                            months[currentDate.getMonth()]
                          ? ""
                          : "gg-w-full",
                        disabled ? "" : "hover:gg-cursor-pointer",
                        "gg-text-lg",
                        "gg-flex gg-items-center",
                      )}
                      disabled={disabled}
                    >
                      <div>
                        {months[currentDate.getMonth()] +
                          " " +
                          currentDate.getFullYear()}
                      </div>
                      <Icon
                        name={IconTypes.ARROW_LEFT}
                        className={concatClassnames(
                          openDropDown ? "gg-rotate-90" : "-gg-rotate-90",
                          "gg-ml-2",
                          "gg-h-2",
                        )}
                      />
                    </button>

                    {openDropDown ? (
                      <ul>
                        {availableDropDownMonths().map(
                          (option, index) =>
                            months[currentDate.getMonth()] !== option &&
                            availableDropDownMonths().indexOf(option) >
                              availableDropDownMonths().indexOf(
                                months[currentDate.getMonth()],
                              ) && (
                              <li
                                className={"hover:gg-cursor-pointer"}
                                onClick={() => {
                                  changeMonth(months.indexOf(option));
                                  changeYear(
                                    getDropdownYear()[
                                      availableDropDownMonths().indexOf(option)
                                    ],
                                  );
                                  dropDownHandler(
                                    months.indexOf(option) + 1,
                                    getDropdownYear()[
                                      availableDropDownMonths().indexOf(option)
                                    ],
                                    "click",
                                  );
                                }}
                                key={option}
                                value={option}
                              >
                                {option + " " + getDropdownYear()[index]}
                              </li>
                            ),
                        )}
                      </ul>
                    ) : null}
                  </div>

                  <button
                    onMouseEnter={() => {
                      nextArrowHandler(date, "hover");
                    }}
                    onClick={() => {
                      nextArrowHandler(date, "click");
                      increaseMonth();
                    }}
                    disabled={nextArrowDisabled(date) || disabled}
                    className={classNames(
                      "gg-mr-2 gg-flex gg-items-center gg-justify-between gg-gap-3 gg-whitespace-nowrap gg-rounded-sm gg-px-5 gg-py-2 gg-text-slate-800 gg-transition gg-duration-300 hover:gg-scale-105 sm:gg-mr-1 sm:gg-w-28",
                      {
                        "gg-invisible": nextArrowDisabled(date),
                      },
                    )}
                  >
                    <span className="gg-hidden sm:gg-flex">Volgende</span>
                    <span>
                      <RightArrow width={16} height={12} />
                    </span>
                  </button>
                </div>
              )}
            />
          </div>
          <div
            className={
              loadingStatus
                ? "gg-z-2 gg-absolute gg-inset-0 gg-flex gg-w-full gg-content-center gg-items-center gg-justify-center gg-gap-x-4 gg-px-5"
                : "gg-invisible gg-absolute"
            }
          >
            <Spinner className="gg-shrink-0 gg-text-black" />
            <h3 className="gg-text-base gg-font-semibold gg-text-dark-blue">
              Momentje, de beschikbare mogelijkheden worden voor je opgehaald.
            </h3>
          </div>
          <div
            className={
              (showUnavailable && !loadingStatus) || refetchAmount > 5
                ? "gg-inset-0 gg-z-[-1] gg-flex gg-content-center gg-items-center gg-justify-center gg-font-bold"
                : "gg-invisible gg-absolute"
            }
          >
            <p className={"gg-m-2"}>Er zijn geen beschikbare datums...</p>
          </div>
        </div>
      </div>

      <div className="gg-col-span-12 gg-bg-dark-blue-200 gg-px-6 gg-py-5">
        {currentDate.getMonth() - thisMonth > 4 && (
          <Text className="gg-mb-4">Jij plant ver vooruit, dat is fijn!</Text>
        )}

        {nextArrowDisabled(currentDate) && slots.length !== 0 && (
          <p className={"gg-relative gg-mb-4"}>
            {
              "Wil je een datum verder in de toekomst vastleggen? Neem dan even contact op met onze klantenservice via "
            }{" "}
            <a
              className={"gg-underline"}
              href="mailto: klantenservice@gewoongers.nl"
            >
              klantenservice@gewoongers.nl
            </a>{" "}
            of{" "}
            <a className={"gg-underline"} href="tel:010 307 28 89">
              010 307 28 89
            </a>
          </p>
        )}

        {nextArrowDisabled(currentDate) && refetchAmount > 5 && (
          <p className={"gg-relative"}>
            {
              "Wil je een datum verder in de toekomst vastleggen? Neem dan even contact op met onze klantenservice via "
            }{" "}
            <a
              className={"gg-underline"}
              href="mailto: klantenservice@gewoongers.nl"
            >
              klantenservice@gewoongers.nl
            </a>{" "}
            of{" "}
            <a className={"gg-underline"} href="tel:010 307 28 89">
              010 307 28 89
            </a>
          </p>
        )}

        <div className={"gg-grid sm:gg-flex"}>
          <span className="gg-mr-4 gg-font-bold">
            {bumbalText}
            <span className="sm:gg-hidden">:</span>
          </span>
          <span>
            {formattedDate ? <>{formattedDate}</> : <>{"Geen datum gekozen"}</>}
          </span>
        </div>
      </div>
    </div>
  );
};
export default Bumbal;
